import { ChecklistItem, ItemStatus } from './ChecklistItem';
import { ChecklistItem as ChecklistItemType } from '../../utils/inspectionChecklistData';

interface ItemState {
  status: ItemStatus;
  notes?: string;
}

interface ChecklistSubsectionProps {
  title: string;
  items: ChecklistItemType[];
  itemStates: Record<string, ItemState>;
  onItemChange: (itemId: string, status: ItemStatus, notes?: string) => void;
}

export function ChecklistSubsection({
  title,
  items,
  itemStates,
  onItemChange,
}: ChecklistSubsectionProps) {
  const getCompletionStats = () => {
    const total = items.length;
    const checked = items.filter((item) => itemStates[item.id]?.status !== 'unchecked').length;
    const passed = items.filter((item) => itemStates[item.id]?.status === 'pass').length;
    const failed = items.filter((item) => itemStates[item.id]?.status === 'fail').length;
    const attention = items.filter((item) => itemStates[item.id]?.status === 'needs_attention').length;

    return { total, checked, passed, failed, attention };
  };

  const stats = getCompletionStats();
  const percentage = stats.total > 0 ? Math.round((stats.checked / stats.total) * 100) : 0;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3 pb-2 border-b-2 border-gray-200">
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600">
            {stats.checked}/{stats.total} ({percentage}%)
          </span>
          {stats.passed > 0 && (
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-md text-xs font-medium">
              {stats.passed} Pass
            </span>
          )}
          {stats.failed > 0 && (
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-md text-xs font-medium">
              {stats.failed} Fail
            </span>
          )}
          {stats.attention > 0 && (
            <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-md text-xs font-medium">
              {stats.attention} Attention
            </span>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {items.map((item) => (
          <ChecklistItem
            key={item.id}
            id={item.id}
            text={item.text}
            status={itemStates[item.id]?.status || 'unchecked'}
            notes={itemStates[item.id]?.notes}
            onStatusChange={(status) => onItemChange(item.id, status, itemStates[item.id]?.notes)}
            onNotesChange={(notes) => onItemChange(item.id, itemStates[item.id]?.status || 'unchecked', notes)}
          />
        ))}
      </div>
    </div>
  );
}
